// The plans you can choose (plan v4 items 2 and 5). Pure, no DB.
//
// BLS routines are the book's Phase One tables exactly (4th ed., p.318-320): every slot
// carries its own sets and rep range, never the exercise library's default (that gap put
// Kenny's dumbbell bench at 4-6 when p.318 says 6-8). "role" lets a choice keep what you
// had: your own swap in a slot (Kenny's leg press for squat), your optional abs and
// calves, and your working weights where the lift, role and range all match.
//
// The glute plan is NOT from the book (Thinner Leaner Stronger is not on the Mac): it is
// a reasoned starting point for Cassidy's goal, built on the same engine, fully editable.

const S = (ex, lo, hi, sets = 3) => ({ ex, lo, hi, sets });

export const ROUTINES = {
  bls5: {
    name: 'Bigger Leaner Stronger, 5 days', short: '5 days', source: 'BLS p.318',
    days: [
      { role: 'push', name: 'Push', weekday: 1, slots: [S('bench', 4, 6), S('incline-bench', 4, 6), S('db-bench', 6, 8), S('pushdown', 6, 8)] },
      { role: 'pull', name: 'Pull', weekday: 2, slots: [S('deadlift', 4, 6), S('one-arm-row', 4, 6), S('lat-pulldown', 6, 8), S('alt-db-curl', 6, 8)] },
      { role: 'upperA', name: 'Upper Body A', weekday: 3, slots: [S('seated-db-press', 4, 6), S('side-raise', 6, 8), S('rear-raise', 6, 8), S('seated-triceps-press', 6, 8)] },
      { role: 'legs', name: 'Legs', weekday: 4, slots: [S('squat', 4, 6), S('leg-curl', 6, 8), S('leg-press', 6, 8), S('db-lunge', 6, 8)] },
      { role: 'upperB', name: 'Upper Body B', weekday: 5, slots: [S('close-grip-bench', 4, 6), S('chin-up', 4, 6), S('seated-cable-row', 6, 8), S('barbell-curl', 6, 8)] },
    ],
  },
  bls4: {
    name: 'Bigger Leaner Stronger, 4 days', short: '4 days', source: 'BLS p.319',
    days: [
      { role: 'push', name: 'Push', weekday: 1, slots: [S('bench', 4, 6), S('incline-bench', 4, 6), S('db-bench', 6, 8), S('pushdown', 6, 8)] },
      { role: 'pull', name: 'Pull', weekday: 2, slots: [S('deadlift', 4, 6), S('one-arm-row', 4, 6), S('lat-pulldown', 6, 8), S('alt-db-curl', 6, 8)] },
      { role: 'upper', name: 'Upper Body', weekday: 4, slots: [S('seated-db-press', 4, 6), S('seated-cable-row', 6, 8), S('close-grip-bench', 6, 8), S('rear-raise', 6, 8)] },
      { role: 'legs', name: 'Lower Body', weekday: 5, slots: [S('squat', 4, 6), S('leg-curl', 6, 8), S('leg-press', 6, 8), S('db-lunge', 6, 8)] },
    ],
  },
  bls3: {
    name: 'Bigger Leaner Stronger, 3 days', short: '3 days', source: 'BLS p.320',
    days: [
      { role: 'push', name: 'Push', weekday: 1, slots: [S('bench', 4, 6), S('incline-bench', 4, 6), S('db-bench', 6, 8), S('pushdown', 6, 8)] },
      { role: 'pull', name: 'Pull', weekday: 3, slots: [S('deadlift', 4, 6), S('one-arm-row', 4, 6), S('lat-pulldown', 6, 8), S('alt-db-curl', 6, 8)] },
      { role: 'legs', name: 'Lower Body', weekday: 5, slots: [S('squat', 4, 6), S('leg-curl', 6, 8), S('leg-press', 6, 8), S('db-lunge', 6, 8)] },
    ],
  },
  glute3: {
    name: 'Glute focus, 3 days', short: '3 days', source: 'reasoned plan, not from the book',
    days: [
      { role: 'gluteA', name: 'Lower A', weekday: 1, slots: [S('hip-thrust', 6, 8), S('rdl', 8, 10), S('bulgarian', 8, 10), S('cable-kickback', 10, 12), S('hip-abduction', 12, 15)] },
      { role: 'upper', name: 'Upper', weekday: 3, slots: [S('lat-pulldown', 8, 10), S('seated-db-press', 8, 10), S('seated-cable-row', 10, 12), S('side-raise', 12, 15)] },
      { role: 'gluteB', name: 'Lower B', weekday: 5, slots: [S('leg-press', 8, 10), S('step-up', 10, 12), S('glute-bridge', 10, 12), S('leg-curl', 10, 12), S('hip-abduction', 15, 20)] },
    ],
  },
  runhiit: {
    name: 'Running + HIIT', short: '2 HIIT days', source: 'your runs stay in the running plan',
    days: [
      { role: 'hiit', name: 'HIIT', weekday: 1, kind: 'hiit', slots: [] },
      { role: 'hiit', name: 'HIIT', weekday: 3, kind: 'hiit', slots: [] },
    ],
  },
};

export const LEG_ROLES = new Set(['legs', 'gluteA', 'gluteB']);
const ROLE_BY_NAME = { push: 'push', pull: 'pull', 'upper body a': 'upperA', 'upper body b': 'upperB', 'upper body': 'upper', upper: 'upper', legs: 'legs', 'lower body': 'legs', 'lower a': 'gluteA', 'lower b': 'gluteB', 'legs a': 'legs', 'legs b': 'legs', hiit: 'hiit' };
export function roleOf(dayName) { return ROLE_BY_NAME[String(dayName || '').trim().toLowerCase()] || null; }
const OPTIONAL_GROUPS = new Set(['abs', 'calves']);

// What applying a routine would do, for review before anything is saved.
//   oldDays: the current program's days, each with .exercises (program-exercise rows joined
//            to .exercise), as loadDayExercises returns them.
//   weekdays: { dayIndex: weekday } overrides.
// Returns { days: [{ name, role, weekday, kind, slots: [{ exercise, lo, hi, sets, rest,
//   startWeight, carryFrom, required, note }] }], notes: [] }.
// note: 'kept' (same lift, role, range: weight and progress carried), 'your-swap' (your
// lift kept in this slot), 'check' (no matching weight; starts from your last one if any),
// 'new' (never done: start weight is yours to set), 'optional' (your abs/calves kept).
export function buildPlanPreview(routineKey, { oldDays = [], library = [], weekdays = {} } = {}) {
  const routine = ROUTINES[routineKey];
  if (!routine) return null;
  const lib = Object.fromEntries(library.map((x) => [x.id, x]));
  const oldByRole = {};
  for (const d of oldDays) { const r = roleOf(d.name); if (r) (oldByRole[r] ||= []).push(...(d.exercises || [])); }
  const anyOld = oldDays.flatMap((d) => d.exercises || []);

  const days = routine.days.map((d, di) => {
    const old = oldByRole[d.role] || [];
    const used = new Set();
    const onDay = new Set();
    const slots = d.slots.map((slot, si) => {
      let ex = lib[slot.ex] || { id: slot.ex, name: slot.ex };
      let note = null;
      // Your own swap: same position in the same role, same movement family, different lift
      // (never the same lift twice in one workout).
      const prev = old[si];
      if (prev && prev.exercise_id !== slot.ex && prev.exercise?.swap_group && prev.exercise.swap_group === ex.swap_group && !onDay.has(prev.exercise_id)) {
        ex = prev.exercise; note = 'your-swap';
      }
      if (onDay.has(ex.id)) { ex = lib[slot.ex] || ex; note = null; }
      onDay.add(ex.id);
      const match = old.find((o) => o.exercise_id === ex.id && o.rep_low === slot.lo && o.rep_high === slot.hi && !used.has(o.id));
      let startWeight = null; let carryFrom = null; let required = '1';
      if (match) {
        used.add(match.id);
        startWeight = match.start_weight_lb ?? null; carryFrom = match.carry_from_lb ?? null; required = match.progression_mode || '1';
        note = note || 'kept';
      } else {
        const any = anyOld.find((o) => o.exercise_id === ex.id && o.start_weight_lb != null);
        startWeight = any ? any.start_weight_lb : null;
        note = note === 'your-swap' ? 'your-swap' : any ? 'check' : 'new';
      }
      const primary = ex.is_primary ?? slot.lo <= 4; // flags follow the BLS p.266-267 table (migration 0006)
      return { exercise: ex, lo: slot.lo, hi: slot.hi, sets: slot.sets, rest: primary ? 180 : 120, increment: ex.default_increment_lb ?? 5, startWeight, carryFrom, required, note };
    });
    // Your optional abs and calves on the matching day ride along (BLS p.268-269).
    for (const o of old) {
      if (!OPTIONAL_GROUPS.has(o.exercise?.muscle_group) || slots.some((s) => s.exercise.id === o.exercise_id)) continue;
      slots.push({ exercise: o.exercise, lo: o.rep_low, hi: o.rep_high, sets: o.target_sets || 3, rest: o.rest_seconds || 120, increment: o.increment_lb ?? 5, startWeight: o.start_weight_lb ?? null, carryFrom: o.carry_from_lb ?? null, required: o.progression_mode || '1', note: 'optional' });
    }
    return { name: d.name, role: d.role, kind: d.kind || 'lift', weekday: Number(weekdays[di] ?? d.weekday), slots };
  });
  return { key: routineKey, name: routine.name, source: routine.source, days };
}

// Two workouts on one weekday is a mistake to catch before saving.
export function weekdayClash(preview) {
  const seen = new Set();
  for (const d of preview?.days || []) { if (seen.has(d.weekday)) return d.weekday; seen.add(d.weekday); }
  return null;
}
